import {
  defaultCountry,
  formatValue,
  normalizeNumber,
  parsePhoneNumber,
} from '@side/pantry';
import { isUSPhoneNumber } from './isUSPhoneNumber';

// Approved phone number formats include:
// NANP: (xxx) xxx-xxxx
// Domestic: xxx-xxx-xxxx
// US E. 164: +1xxxxxxxxxx
// Unformatted: xxxxxxxxxx
const APPROVED_FORMATS_TO_DEFAULT_TO_US =
  /^(\+1)?\(?\d{3}\)?[-\s]?\d{3}[-\s]?\d{4}$/;

export const derivePhoneNumber = (phoneNumber?: string | null) => {
  if (!phoneNumber) {
    return '';
  }

  if (phoneNumber.startsWith('+')) {
    return parsePhoneNumber(phoneNumber).number;
  }

  if (APPROVED_FORMATS_TO_DEFAULT_TO_US.test(phoneNumber)) {
    return parsePhoneNumber(phoneNumber, defaultCountry).number;
  }

  return '';
};

/**
 * Parses a value for display: an E.164 number of any country, or a US/NANP number
 * in an approved format — the same acceptance as `derivePhoneNumber`. Returns null
 * for anything else.
 */
const parseForDisplay = (value: string) => {
  if (value.startsWith('+')) {
    return parsePhoneNumber(value);
  }

  if (APPROVED_FORMATS_TO_DEFAULT_TO_US.test(value)) {
    return parsePhoneNumber(value, defaultCountry);
  }

  return null;
};

/**
 * Formats a phone number for display using its country's full mask, so the
 * dialing code is always shown: US/NANP renders as "+1 (555) 555-5555" and
 * international keeps its code and country-specific grouping, e.g. Brazil as
 * "+55 (21) 98558-0759". Unrecognized input is returned unchanged. (CORE-15340)
 * @param phoneNumber the number to attempt to format
 * @returns string
 */
export const formatPhoneNumber = (phoneNumber: string) => {
  const parsed = parseForDisplay(phoneNumber);
  if (!parsed) {
    return phoneNumber;
  }

  const prefixDigits = parsed.country.prefix.replace(/[^0-9]/g, '');
  return formatValue(
    `${prefixDigits}${parsed.nationalNumber}`,
    parsed.country.mask.full,
  );
};

/**
 * Formats only the national part of a number with its country's national mask,
 * dropping the dialing code (e.g. "+15555555555" -> "(555) 555-5555"). Returns
 * null for empty input. Prefer `formatPhoneNumber`; this exists for the rare
 * display that intentionally omits the country code (e.g. the partner map).
 */
export const formatPhoneNumberWithoutPrefix = (value: string | null) => {
  if (!value) {
    return null;
  }

  const parsed = parsePhoneNumber(value);
  return formatValue(parsed.nationalNumber, parsed.country.mask.national);
};

/**
 * Normalizes a raw US phone number for autofill by prepending the +1 country
 * code if the number matches the NANP format and doesn't already have one.
 * Returns the value unchanged if it isn't a recognized US number.
 */
export const normalizeUSPhoneForAutofill = (value: string): string => {
  if (
    isUSPhoneNumber(value) &&
    !value.startsWith('1') &&
    !value.startsWith('+1')
  ) {
    return `+1${normalizeNumber(value)}`;
  }
  return value;
};

export const isValueEqualToCountryPrefix = (value = '') => {
  const result = parsePhoneNumber(value);
  return value.replace('+', '').replace('x', '') === result.country.prefix;
};
